/* Scandinavian Edition — custom collection filters (see snippets/se-filters.liquid).
   - se-temp-facet: temperature buttons (or slider) → "start ≤ t" and "end ≥ t" metafield values.
   - se-weather-facet: Waterproof / …resistant checkboxes → the matching metafield values.
   - se-facet-remove: active-filter pill that can remove several URL values at once.
   - Choosing a size also ticks "In stock", so the grid only shows products where that size
     can actually be bought. */

(() => {
  const fmt = (v) => (v < 0 ? `−${Math.abs(v)}°` : v > 0 ? `+${v}°` : '0°');

  const hidden = (name, value) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = String(value);
    return input;
  };

  const formFor = (el) => {
    // The toolbar slider sits outside the form; its inputs point to it with form="…"
    const owner = el.querySelector?.('input[form]')?.form;
    return (
      owner?.closest('facets-form-component') ||
      el.closest('facets-form-component') ||
      document.querySelector('facets-form-component')
    );
  };

  // All handlers are delegated from the document, so they keep working however the
  // theme re-renders (morphs) the filter section after each change.
  const numbers = (text) => (text || '').split(',').filter(Boolean).map(Number);
  const stopAt = (facet, range) => {
    const stops = numbers(facet.dataset.stops);
    return stops[Math.max(0, Math.min(stops.length - 1, Number(range.value)))];
  };

  const applyTemp = (facet, t) => {
    const box = facet.querySelector('[data-se-temp-inputs]');
    if (!box) return;
    const active = t !== null && t !== undefined;
    // One hidden input per value: Shopify reads repeated parameters
    const inputs = [];
    if (active) {
      for (const v of numbers(facet.dataset.stops).filter((s) => s <= t)) inputs.push(hidden(facet.dataset.startName, v));
      for (const v of numbers(facet.dataset.ends).filter((e) => e >= t)) inputs.push(hidden(facet.dataset.endName, v));
    }
    box.replaceChildren(...inputs);
    facet.classList.toggle('se-temp--active', active);
    formFor(facet)?.updateFilters?.();
  };

  document.addEventListener('input', (event) => {
    const range = event.target.closest?.('[data-se-temp-range]');
    const facet = range?.closest('se-temp-facet');
    if (!facet) return;
    const label = fmt(stopAt(facet, range));
    const out = facet.querySelector('[data-se-temp-output]');
    if (out) out.textContent = label;
    range.setAttribute('aria-valuetext', label);
  });

  document.addEventListener('change', (event) => {
    const range = event.target.closest?.('[data-se-temp-range]');
    const facet = range?.closest('se-temp-facet');
    if (facet) {
      event.stopPropagation();
      applyTemp(facet, stopAt(facet, range));
      return;
    }
    const weather = event.target.closest?.('se-weather-facet');
    if (weather && event.target.type === 'checkbox') {
      const box = event.target.closest('li')?.querySelector('[data-se-weather-inputs]');
      if (!box) return;
      event.stopPropagation();
      const values = event.target.checked ? numbers(box.dataset.values) : [];
      box.replaceChildren(...values.map((v) => hidden(box.dataset.name, v)));
      formFor(weather)?.updateFilters?.();
    }
  });

  // ---------- Length (cm): two handles ----------
  const lenState = (facet) => {
    const lo = facet.querySelector('[data-se-len-lo]');
    const hi = facet.querySelector('[data-se-len-hi]');
    const min = Number(facet.dataset.min);
    const max = Number(facet.dataset.max);
    return { lo, hi, min, max, a: Math.min(Number(lo.value), Number(hi.value)), b: Math.max(Number(lo.value), Number(hi.value)) };
  };
  const lenPaint = (facet) => {
    const { a, b, min, max } = lenState(facet);
    const span = max - min || 1;
    const fill = facet.querySelector('[data-se-len-fill]');
    if (fill) {
      fill.style.left = `${((a - min) / span) * 100}%`;
      fill.style.right = `${100 - ((b - min) / span) * 100}%`;
    }
    const out = facet.querySelector('[data-se-len-output]');
    if (out) out.textContent = `${a}–${b} cm`;
  };
  const lenApply = (facet, clear) => {
    const { a, b, min, max } = lenState(facet);
    const box = facet.querySelector('[data-se-len-inputs]');
    const all = clear || (a <= min && b >= max);
    const values = all ? [] : numbers(facet.dataset.values).filter((v) => v >= a && v <= b);
    // Nothing in range: submit an impossible value so the grid shows "no products" instead of everything
    box.replaceChildren(...(all ? [] : (values.length ? values : [-1]).map((v) => hidden(facet.dataset.name, v))));
    formFor(facet)?.updateFilters?.();
  };
  document.querySelectorAll('se-len-facet').forEach(lenPaint);
  new MutationObserver(() => document.querySelectorAll('se-len-facet:not([data-painted])').forEach((f) => {
    f.dataset.painted = '';
    lenPaint(f);
  })).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener('input', (event) => {
    const facet = event.target.closest?.('se-len-facet');
    if (!facet || event.target.type !== 'range') return;
    lenPaint(facet);
  });
  document.addEventListener('change', (event) => {
    const facet = event.target.closest?.('se-len-facet');
    if (!facet || event.target.type !== 'range') return;
    event.stopPropagation();
    lenApply(facet, false);
  }, true);
  document.addEventListener('click', (event) => {
    const clear = event.target.closest?.('[data-se-len-clear]');
    if (!clear) return;
    const facet = clear.closest('se-len-facet');
    if (facet) lenApply(facet, true);
  });

  const removeFrom = (pill) => {
    let list = [];
    try {
      list = JSON.parse(pill.dataset.remove || '[]');
    } catch (e) {
      return;
    }
    const url = new URL(window.location.href);
    for (const { name, values } of list) {
      const current = url.searchParams.getAll(name).flatMap((v) => v.split(','));
      url.searchParams.delete(name);
      if (Array.isArray(values)) {
        for (const v of current.filter((v) => !values.includes(v))) url.searchParams.append(name, v);
      }
    }
    url.searchParams.delete('page');
    formFor(pill)?.updateFiltersByURL?.(url.toString());
  };

  document.addEventListener('click', (event) => {
    const band = event.target.closest?.('[data-se-temp-band]');
    if (band) {
      const facet = band.closest('se-temp-facet');
      if (!facet) return;
      // Same button again = off
      const on = band.getAttribute('aria-pressed') === 'true';
      facet.querySelectorAll('[data-se-temp-band]').forEach((b) => {
        b.setAttribute('aria-pressed', String(!on && b === band));
        b.classList.toggle('is-on', !on && b === band);
      });
      applyTemp(facet, on ? null : Number(band.dataset.seTempBand));
      return;
    }
    const clear = event.target.closest?.('[data-se-temp-clear]');
    if (clear) {
      const facet = clear.closest('se-temp-facet');
      if (facet) applyTemp(facet, null);
      return;
    }
    const pill = event.target.closest?.('se-facet-remove');
    if (pill) removeFrom(pill);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const pill = event.target.closest?.('se-facet-remove');
    if (!pill) return;
    event.preventDefault();
    removeFrom(pill);
  });

  // ---------- Filter panel toggle (desktop) ----------
  const KEY = 'se-filters-open';
  const root = document.documentElement;
  const read = () => {
    try {
      return localStorage.getItem(KEY) === '1';
    } catch (e) {
      return false;
    }
  };
  const write = (open) => {
    try {
      localStorage.setItem(KEY, open ? '1' : '0');
    } catch (e) {}
  };
  const sync = () => {
    const open = root.classList.contains('se-filters-open');
    document.querySelectorAll('[data-se-filter-toggle]').forEach((button) => {
      button.setAttribute('aria-expanded', String(open));
      const label = button.querySelector('.se-filter-toggle__label');
      if (label) label.textContent = open ? label.dataset.labelClose : label.dataset.labelOpen;
    });
  };
  const setOpen = (open) => {
    root.classList.toggle('se-filters-open', open);
    write(open);
    sync();
  };

  if (read()) root.classList.add('se-filters-open');
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-se-filter-toggle]');
    if (!button) return;
    setOpen(!root.classList.contains('se-filters-open'));
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !root.classList.contains('se-filters-open')) return;
    if (!event.target.closest?.('[data-se-filter-panel]')) return;
    setOpen(false);
    document.querySelector('[data-se-filter-toggle]')?.focus();
  });
  // Section re-renders replace the button; keep its state in step
  let pending = 0;
  new MutationObserver(() => {
    cancelAnimationFrame(pending);
    pending = requestAnimationFrame(sync);
  }).observe(document.documentElement, { childList: true, subtree: true });
  sync();


  // Size chosen → also "In stock" (runs before the theme's own change handler)
  const SIZE = /^filter\.v\.option\.(size|st(o|ø)rrelse)$/i;
  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.checked || !SIZE.test(input.name)) return;
      const form = input.form || input.closest('form');
      if (!form) return;
      const inStock = form.querySelector('input[name="filter.v.availability"][value="1"]');
      if (inStock instanceof HTMLInputElement) {
        if (!inStock.checked && !inStock.disabled) inStock.checked = true;
      } else if (!form.querySelector('input[data-se-auto-stock]')) {
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'filter.v.availability';
        hidden.value = '1';
        hidden.dataset.seAutoStock = '';
        form.append(hidden);
      }
    },
    true
  );
})();
